import { handleAPIResponse, getAuthHeaders } from '@/utils/apiUtils';
import {
  SearchCandidatesResponse,
  Candidate,
  Interview,
  SuggestAndFetchHistoryResponse,
  TimelineItem,
} from '@/types/search';
import { Filters } from '@/components/search/FilterModal';
import { CandidateNote } from '@/contexts/CandidateContext';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL;

export class ApiError extends Error {
  constructor(
    message: string,
    public originalError?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const searchApi = {
  searchCandidates: async (query: string, filters?: Filters): Promise<SearchCandidatesResponse> => {
    const response = await fetch(`${API_BASE_URL}/search`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ query, filters }),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        // If response is not JSON or empty
        errorData = { message: `HTTP error ${response.status}` };
      }
      throw new Error(errorData.message || `Search failed with status ${response.status}`);
    }

    // If response is OK, parse and return the full JSON body
    const fullResponseData: SearchCandidatesResponse = await response.json();
    return fullResponseData;
  },

  suggestCandidatesAndFetchHistory: async (): Promise<SuggestAndFetchHistoryResponse> => {
    const response = await fetch(`${API_BASE_URL}/search/init`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: `HTTP error ${response.status}` };
      }
      throw new Error(
        errorData.message || `Failed to get initial search data with status ${response.status}`
      );
    }

    const data: SuggestAndFetchHistoryResponse = await response.json();
    return data;
  },

  getCandidateById: async (id: string | number, jobId?: string, includeTimeline?: boolean) => {
    const params = new URLSearchParams();
    if (jobId) params.set('jobId', jobId);
    if (includeTimeline) params.set('includeTimeline', 'true');

    const urlParams = params.toString();
    const url = urlParams
      ? `${API_BASE_URL}/candidates/${id}?${urlParams}`
      : `${API_BASE_URL}/candidates/${id}`;

    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    return handleAPIResponse<{ 
      candidate: Candidate; 
      timeline?: TimelineItem[];
      timelineCount?: number;
      notesCount?: number;
      aiInterviewScheduledAt?: string;
      hasCompletedAiInterview?: boolean;
    }>(
      response
    );
  },

  reanalyzeInterview: async (interviewId: string): Promise<Interview> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/candidates/interview/${interviewId}/reanalyse-final`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
        }
      );
      return handleAPIResponse<Interview>(response);
    } catch (error) {
      throw new ApiError('Failed to reanalyze interview', error);
    }
  },

  // Fetch candidate notes for a specific job
  fetchCandidateNotes: async (jobId: string, candidateId: string): Promise<CandidateNote[]> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/jobs/${jobId}/candidates/${candidateId}/notes`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
        }
      );

      // Handle the new response structure with notes array
      const data = await handleAPIResponse<{ notes: CandidateNote[] }>(response);
      return data.notes || [];
    } catch (error) {
      throw new ApiError('Failed to fetch candidate notes', error);
    }
  },

  // Add a note to a candidate for a specific job
  addCandidateNote: async (
    jobId: string,
    candidateId: string,
    content: string,
    mentions?: Array<{ employeeId: string; employeeName: string }>
  ): Promise<CandidateNote> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/jobs/${jobId}/candidates/${candidateId}/notes`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            note: content,
            mentions,
          }),
        }
      );
      // Handle the new response structure
      const data = await handleAPIResponse<{ note: CandidateNote }>(response);
      return data.note;
    } catch (error) {
      throw new ApiError('Failed to add candidate note', error);
    }
  },

  // Update a note for a candidate for a specific job
  updateCandidateNote: async (
    jobId: string,
    candidateId: string,
    noteId: string,
    content: string,
    mentions?: Array<{ employeeId: string; employeeName: string }>
  ): Promise<CandidateNote> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/jobs/${jobId}/candidates/${candidateId}/notes/${noteId}`,
        {
          method: 'PUT',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            note: content,
            mentions,
          }),
        }
      );
      // Handle the new response structure
      const data = await handleAPIResponse<{ note: CandidateNote }>(response);
      return data.note;
    } catch (error) {
      throw new ApiError('Failed to update candidate note', error);
    }
  },

  // Get candidate report
  getCandidateReport: async (
    candidateId: string,
    jobId?: string
  ): Promise<{ reportUrl: string }> => {
    try {
      const url = jobId
        ? `${API_BASE_URL}/candidates/${candidateId}/report?jobId=${encodeURIComponent(jobId)}`
        : `${API_BASE_URL}/candidates/${candidateId}/report`;

      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });

      return handleAPIResponse<{ reportUrl: string }>(response);
    } catch (error) {
      console.error('Error getting candidate report:', error);
      throw new ApiError('Failed to generate candidate report', error);
    }
  },
};
